//
// ImageLayoutBuilder
//
// Based on view info from a layout

import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useElementWidth } from '../../../../hooks/useElementWidth'
import { useStore } from '../../../../stores'

import { AssetThumb, IconTile } from '../../../../panels'
import { mergeTransforms, Transform } from '../../../../stores/imageservers/utils'

export const ImageLayoutBuilder = observer(function ImageLayoutBuilder({
    className,
    component,
    components,
    layout,
    record,
    worksheet,
    active_class,
    visited_gids,
    force_readonly,
}) {
    const { data } = useStore()
    const [containerRef, containerWidth] = useElementWidth(250)

    let attributes = { className: 'layout-component' }
    if (className) attributes['className'] += ' ' + className
    if (component.style) attributes['className'] += ' lc-' + component.style
    if (component.gid === worksheet.layoutstore.selectedComponentGid) {
        attributes['className'] += ' cc-selected'
    }

    let value = null
    if ('image' in component) value = component['image']

    const [asset, setAsset] = useState(null)
    useEffect(() => {
        let isMounted = true
        let controller = new AbortController()
        async function asyncEffect() {
            setAsset(null)
            if (value) {
                if (!data.assets.has(value)) {
                    await data.fetchAssetByGid(value, { signal: controller.signal })
                }
                const asyncasset = data.assets.get(value)
                if (isMounted) {
                    setAsset(asyncasset)
                }
            }
        }
        asyncEffect()
        return () => {
            controller.abort('Unmounted')
            isMounted = false
        }
    }, [value, data])

    const componentformat =
        component && component.thumbformat ? component.thumbformat : undefined

    // default: fit, square
    let transform_fit = 'fit'
    let transform_width = containerWidth
    let transform_height = transform_width

    if (componentformat) {
        if (componentformat.startsWith('fill_')) {
            transform_fit = 'fill'
        }
        if (componentformat.endsWith('_banner')) {
            transform_height = (6 / 20) * transform_width
        }
        if (componentformat.endsWith('_landscape')) {
            transform_height = (9 / 16) * transform_width
        }
        if (componentformat.endsWith('_portrait')) {
            transform_height = (16 / 9) * transform_width
        }
        if (componentformat.endsWith('_pillar')) {
            transform_height = (20 / 6) * transform_width
        }
        if (componentformat === 'proportional' && asset) {
            const asset_width = asset.info.get('width')
            const asset_height = asset.info.get('height')
            if (asset_width && asset_height) {
                transform_height = (asset_height / asset_width) * transform_width
            }
        }
    }

    const transformResize = new Transform(
        transform_fit,
        transform_width,
        transform_height
    )
    const transform = asset
        ? mergeTransforms(asset.mainTransform, transformResize)
        : transformResize

    return (
        <div {...attributes} ref={containerRef}>
            {!asset ? (
                <IconTile
                    width={transform_width}
                    className="ws-tile cc-dimmed"
                    icon="image"
                    anchor="center"
                />
            ) : (
                <AssetThumb
                    asset={asset}
                    transform={transform}
                    iconplaceholder="image"
                />
            )}
        </div>
    )
})
